import type { ImpersonationMerchantRole, ImpersonationTarget, ImpersonationTargetWorkspace } from "@unbox/api";
import type { BadgeColors } from "@/components/base/badges/badge-types";
import { SWITCHER_COPY } from "./constants";

export type ListUser = ImpersonationTarget;
export type Row = { user: ListUser; workspaces: ImpersonationTargetWorkspace[] };

export const MERCHANT_ROLES: ImpersonationMerchantRole[] = ["workspace_admin", "merchant_admin", "user"];

const ROLE_LABELS: Record<ImpersonationMerchantRole, string> = {
  workspace_admin: "Workspace admin",
  merchant_admin: "Merchant admin",
  user: "User",
};

export const roleLabel = (role: ImpersonationMerchantRole): string => ROLE_LABELS[role];

export const fullName = (user: ListUser): string =>
  [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

export const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export const isUnboxStaff = (user: ListUser): boolean => user.email.toLowerCase().endsWith("@unbox-commerce.com");

export const merchantCount = (workspaces: ImpersonationTargetWorkspace[]): number =>
  workspaces.reduce((total, workspace) => total + workspace.merchants.length, 0);

/** One label for a workspace: the merchant role when all grants agree, otherwise "Multiple roles". */
export const workspaceRoleSummary = (workspace: ImpersonationTargetWorkspace): string => {
  const roles = MERCHANT_ROLES.filter((role) => workspace.merchants.some((merchant) => merchant.role === role));
  if (roles.length > 1) return "Multiple roles";
  if (roles.length === 1) return roleLabel(roles[0]);
  return workspace.role === "workspace_admin" ? roleLabel("workspace_admin") : "No access";
};

export const roleSummaryColor = (summary: string): BadgeColors => {
  if (summary === "Multiple roles") return "warning";
  if (summary === "No access") return "gray";
  return "brand";
};

export const merchantsByRole = (
  workspace: ImpersonationTargetWorkspace,
): { role: ImpersonationMerchantRole; names: string[] }[] =>
  MERCHANT_ROLES.map((role) => ({
    role,
    names: workspace.merchants.filter((merchant) => merchant.role === role).map((merchant) => merchant.name),
  })).filter((group) => group.names.length > 0);

export type FilterOption = { value: string; label: string; count: number };

export const workspaceOptions = (users: ListUser[]): FilterOption[] => {
  const options = new Map<string, FilterOption>();
  for (const user of users) {
    for (const workspace of user.workspaces) {
      const option = options.get(workspace.id) ?? { value: workspace.id, label: workspace.name, count: 0 };
      option.count += 1;
      options.set(workspace.id, option);
    }
  }
  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
};

export const merchantOptions = (users: ListUser[]): FilterOption[] => {
  const options = new Map<string, FilterOption>();
  for (const user of users) {
    const seen = new Set<string>();
    for (const workspace of user.workspaces) {
      for (const merchant of workspace.merchants) {
        if (seen.has(merchant.id)) continue;
        seen.add(merchant.id);
        const option = options.get(merchant.id) ?? { value: merchant.id, label: merchant.name, count: 0 };
        option.count += 1;
        options.set(merchant.id, option);
      }
    }
  }
  return [...options.values()].sort((a, b) => a.label.localeCompare(b.label));
};

export type Filters = {
  query: string;
  workspaces: string[];
  merchants: string[];
  roles: ImpersonationMerchantRole[];
};

export const EMPTY_FILTERS: Filters = { query: "", workspaces: [], merchants: [], roles: [] };

export const activeFilterCount = (filters: Filters): number =>
  filters.workspaces.length + filters.merchants.length + filters.roles.length;

const includes = (haystack: string, needle: string) => haystack.toLowerCase().includes(needle);

/** Keeps a user when at least one of their workspaces passes the query and every filter. */
export const filterRows = (users: ListUser[], filters: Filters): Row[] => {
  const query = filters.query.trim().toLowerCase();
  return users
    .map((user) => {
      const userMatches = !query || includes(`${fullName(user)} ${user.email} ${user.id}`, query);
      const workspaces = user.workspaces.filter((workspace) => {
        if (query && !userMatches) {
          const workspaceMatches =
            includes(`${workspace.name} ${workspace.id}`, query) ||
            workspace.merchants.some((merchant) => includes(merchant.name, query));
          if (!workspaceMatches) return false;
        }
        if (filters.workspaces.length && !filters.workspaces.includes(workspace.id)) return false;
        if (filters.merchants.length && !workspace.merchants.some((m) => filters.merchants.includes(m.id)))
          return false;
        if (filters.roles.length && !workspace.merchants.some((m) => filters.roles.includes(m.role))) return false;
        return true;
      });
      return { user, workspaces };
    })
    .filter((row) => row.workspaces.length > 0);
};

export type Group = {
  key: string;
  /** Left out, the group is listed without a heading. */
  label: string | null;
  star?: boolean;
  rows: Row[];
};

/** Favourites first, then everyone else. */
export const groupRows = (rows: Row[], favourites: string[]): Group[] => {
  const isFavourite = (row: Row) => favourites.includes(row.user.id);
  return [
    { key: "fav", label: "Favourites", star: true, rows: rows.filter(isFavourite) },
    { key: "users", label: SWITCHER_COPY.listLabel, rows: rows.filter((row) => !isFavourite(row)) },
  ].filter((group) => group.rows.length > 0);
};

/**
 * The sign-in a scenario's user gets. It sits on its own subdomain so a scenario
 * named after a colleague cannot take their real address.
 */
export const slugEmail = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ".")
    .replace(/^\.+|\.+$/g, "");
  return slug ? `${slug}@scenarios.unbox-commerce.com` : "";
};
